import { useTranslation } from '@bluebrick/saas-ui'
import { type DateTime } from 'luxon'
import { computed, type MaybeRef, unref } from 'vue'
import { useToday } from '@/common/composables/useToday'
import { dateTimeFormattedShort } from '@/common/dateUtils'
import type { SaleDaysSummaryPeriod } from '@/features/preferences/types'
import { type PeriodsDropdown } from '@/features/saleDaysSummary/types'

export function useScorePeriods(selectedPeriod?: MaybeRef<SaleDaysSummaryPeriod>) {
	const { t } = useTranslation()
	const { today } = useToday()
	const lastYear = computed(() => today.value.minus({ year: 1 }))

	function getPeriodItemForDropdown(id: SaleDaysSummaryPeriod, startDate: DateTime<true>, endDate: DateTime<true>) {
		const name = t(`saleDaysSummaryPanel.${id}`)
		const formattedDates = `${dateTimeFormattedShort(startDate)} - ${dateTimeFormattedShort(endDate)}`

		return {
			id,
			name: `${name} (${formattedDates})`,
			parent: 'root',
			data: {
				period: id,
				startDate,
				endDate
			},
			selected: unref(selectedPeriod) === id
		}
	}

	const periods = computed(
		() =>
			({
				items: [
					getPeriodItemForDropdown('last-year', lastYear.value.startOf('year'), lastYear.value.endOf('year')),
					getPeriodItemForDropdown('this-year', today.value.startOf('year'), today.value.endOf('day')),
					// The "current" period is last 45 days - next 30 days
					getPeriodItemForDropdown('current', today.value.minus({ days: 45 }).startOf('day'), today.value.plus({ days: 30 }).endOf('day')),
					getPeriodItemForDropdown('next-4-weeks', today.value.startOf('day'), today.value.plus({ weeks: 4 }).endOf('day'))
				]
			}) as PeriodsDropdown
	)

	const defaultPeriod: SaleDaysSummaryPeriod = 'current'
	const defaultStartDate = today.value.minus({ days: 45 }).startOf('day')
	const defaultEndDate = today.value.plus({ days: 30 }).endOf('day')

	return {
		periods,
		defaultPeriod,
		defaultStartDate,
		defaultEndDate
	}
}
